import React from 'react';

import { Box, Button, Paper, Typography } from '@mui/material';
import {
  ArrowCheckIcon,
  ConfirmIcon,
  LeftRightExpandedIcon,
  LeftRightIcon,
  PlusIcon,
} from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import SearchInputCDS from 'app/shared/componentsRestyled/coreDesignSystem/SearchInputCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconOrientation, IconSize } from 'constants/coreDesignSystem';
import { COLOR_PALETTE } from 'constants/ui';
import useSearch from 'hooks/useSearch';

import { DocumentTestIds } from '../document.testIds';

const defaultObject = {};

const controlsWrapperSx = {
  marginBottom: '10px',
  padding: '0 15px',
  position: 'relative',
};

const iconButtonsRowSx = { display: 'flex', alignItems: 'center', gap: '8px' };

const filterChipRowSx = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'nowrap',
  overflowX: 'auto',
};

const filterChipActiveSx = {
  backgroundColor: COLOR_PALETTE.BRAND[1],
  borderColor: COLOR_PALETTE.BRAND[3],
  color: COLOR_PALETTE.BRAND[7],
  fontWeight: 500,
  '&:hover': {
    backgroundColor: COLOR_PALETTE.BRAND[2],
  },
};

const filterChipBaseSx = {
  fontSize: '11px',
  padding: '2px 8px',
  borderRadius: '16px',
  textTransform: 'none',
  minWidth: 0,
  lineHeight: '18px',
  height: 'auto',
  gap: '6px',
};

const filterChipOnSx = { ...filterChipBaseSx, ...filterChipActiveSx };

const titleSx = { flex: 1 };

const countSpanSx = {
  color: COLOR_PALETTE.GRAY[6],
  fontWeight: 500,
};

const collapseIconButtonSx = {
  minWidth: 0,
  width: '32px',
  height: '32px',
  padding: 0,
  borderRadius: '8px',
  border: `1px solid ${COLOR_PALETTE.GRAY[4]}`,
  color: COLOR_PALETTE.GRAY[7],
  '&:hover': {
    backgroundColor: COLOR_PALETTE.GRAY[3],
  },
};

const primaryUploadButtonSx = {
  minWidth: 0,
  width: '32px',
  height: '32px',
  padding: 0,
  borderRadius: '8px',
};

const CHIP_CHECK_ICON_SIZE = 9;

const chipCheckOnSx = {
  width: '13px',
  height: '13px',
  borderRadius: '3px',
  border: `1.5px solid ${COLOR_PALETTE.BRAND[6]}`,
  backgroundColor: COLOR_PALETTE.BRAND[6],
  color: COLOR_PALETTE.GRAY[1],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const chipCheckOffSx = {
  width: '13px',
  height: '13px',
  borderRadius: '3px',
  border: `1.5px solid ${COLOR_PALETTE.GRAY[5]}`,
  backgroundColor: 'transparent',
  flexShrink: 0,
};

const DocumentsTreeHeader = ({
  iconButtonsConfig = null,
  checkboxesConfig = null,
  onCollapse = _.noop,
  onCollapsePanelLeft = null,
  isAllExpanded = true,
  toolbarOptions = defaultObject,
  totalDocumentCount = 0,
}) => {
  const { setSearchText } = useSearch();

  const isIconButtonsAvailable = !!iconButtonsConfig?.length;
  const isCheckboxesAvailable = !!checkboxesConfig?.length;
  const hasAiBatchIntake = !!toolbarOptions.onOpenAiBatchIntake;

  return (
    <Paper square className="full-width" elevation={3} sx={controlsWrapperSx}>
      <Box className="py-4" sx={iconButtonsRowSx}>
        <Typography sx={titleSx} variant="bold14">
          All Documents{' '}
          <Typography component="span" sx={countSpanSx} variant="bold14">
            · {totalDocumentCount}
          </Typography>
        </Typography>
        {isIconButtonsAvailable &&
          iconButtonsConfig.map((iconButtonProps, index) => (
            <ButtonWithTooltip
              key={index}
              {...iconButtonProps}
              data-testid={
                iconButtonProps['data-testid'] ?? DocumentTestIds.getTreeHeaderIconButtonId(index)
              }
            />
          ))}
        {onCollapse && (
          <Button
            data-testid={DocumentTestIds.TREE_HEADER_COLLAPSE_BUTTON}
            size="small"
            sx={collapseIconButtonSx}
            title={isAllExpanded ? 'Collapse all' : 'Expand all'}
            variant="outlined"
            onClick={onCollapse}
          >
            {isAllExpanded ? (
              <LeftRightExpandedIcon orientation={IconOrientation.VERTICAL} size={IconSize[20]} />
            ) : (
              <LeftRightIcon orientation={IconOrientation.VERTICAL} size={IconSize[20]} />
            )}
          </Button>
        )}
        {onCollapsePanelLeft && (
          <ButtonWithTooltip
            data-testid={DocumentTestIds.TREE_HEADER_COLLAPSE_PANEL_BUTTON}
            icon={ArrowCheckIcon}
            iconDirection={IconDirection.LEFT}
            iconSize={IconSize[20]}
            size="small"
            title="Collapse documents list"
            variant="outlined"
            onClick={onCollapsePanelLeft}
          />
        )}
        {hasAiBatchIntake && (
          <Button
            data-testid={DocumentTestIds.AI_BATCH_INTAKE_BUTTON}
            size="small"
            sx={primaryUploadButtonSx}
            title="Upload / AI sort"
            variant="contained"
            onClick={toolbarOptions.onOpenAiBatchIntake}
          >
            <PlusIcon size={IconSize[20]} />
          </Button>
        )}
      </Box>

      <div className="py-4">
        <SearchInputCDS
          focusOnType
          searchOnType
          ariaLabel="Search documents"
          onSearch={setSearchText}
        />
      </div>

      <div className="py-4">
        {isCheckboxesAvailable && (
          <Box sx={filterChipRowSx}>
            {checkboxesConfig.map(({ label, value, onChange, 'data-testid': testId }, index) => (
              <Button
                data-testid={testId}
                key={index}
                size="small"
                sx={value ? filterChipOnSx : filterChipBaseSx}
                variant="outlined"
                onClick={onChange}
              >
                <Box component="span" sx={value ? chipCheckOnSx : chipCheckOffSx}>
                  {value && <ConfirmIcon size={CHIP_CHECK_ICON_SIZE} />}
                </Box>
                {label}
              </Button>
            ))}
          </Box>
        )}
      </div>
    </Paper>
  );
};

DocumentsTreeHeader.propTypes = {
  checkboxesConfig: PropTypes.array,
  iconButtonsConfig: PropTypes.array,
  isAllExpanded: PropTypes.bool,
  toolbarOptions: PropTypes.object,
  totalDocumentCount: PropTypes.number,
  onCollapse: PropTypes.func,
  onCollapsePanelLeft: PropTypes.func,
};

export default DocumentsTreeHeader;
